// @flow
import styles from './JobMail.less';

import { connect } from 'react-redux';
import React, { Component } from 'react';
import classnames from 'classnames';

import { Button, EmailField, TextLink, Text } from 'seek-style-guide/react';
import NavLink from 'src/components/NavLink/NavLink';

import { jobMail, emailChange, states } from 'src/store/saveSearch/saveSearch';

type CreateHandlerProps = {
  locale: Locale,
  query: SearchParams,
  location?: UrlLocation,
  emailAddress: string,
  jobMailType: JobMailType
};

type CreateHandler = (creatHandlerProps: CreateHandlerProps) => void;

type Props = {
  locale: Locale,
  pageNumber: number,
  emailAddress?: string,
  query: SearchParams,
  position: JobMailPosition,
  location: UrlLocation,
  status: string,
  errorMessage: string,
  createHandler: CreateHandler,
  changeHandler: Function
};

type State = {|
  emailAddressFocused: boolean
|};

const { SAVED, ERROR, SAVING } = states;

const dataAutomationPrefix = 'jobmail';

const noop = () => {};
const mapPositionToJobMailType = ({
  position,
  pageNumber
}: {
  position: JobMailPosition,
  pageNumber: number
}): JobMailType =>
  ({
    top: pageNumber === 1 ? 'job-mail-top-page-1' : 'job-mail-after-page-1',
    bottom: 'job-mail'
  }[position]);

const mapStateToProps = ({ search, results, saveSearch, location }) => ({
  locale: location.locale,
  query: search.query,
  location: results.location,
  emailAddress: saveSearch.emailAddress,
  status: saveSearch.status,
  errorMessage: saveSearch.errorMessage,
  pageNumber: location.pageNumber
});

const mapDispatchToProps = dispatch => ({
  createHandler: ({ locale, query, location, emailAddress, jobMailType }) => {
    dispatch(jobMail({ locale, query, location, emailAddress, jobMailType }));
  },
  changeHandler: () => dispatch(emailChange())
});

class JobMail extends Component<Props, State> {
  state = {
    emailAddressFocused: false
  };

  emailField: any; /* DOM element */
  jobMailClick: (SyntheticEvent<*>) => void;
  storeEmailFieldReference: any /* DOM element */ => void;
  emailFieldFocusHandler: void => void;

  emailFieldFocusHandler = () => {
    this.setState({
      emailAddressFocused: true
    });
  };

  storeEmailFieldReference = emailField => {
    if (emailField !== null) {
      this.emailField = emailField.input;
    }
  };

  jobMailClick = ev => {
    ev.preventDefault();
    const {
      createHandler,
      locale,
      query,
      location,
      position,
      pageNumber
    } = this.props;
    const emailAddress = this.emailField.value;

    createHandler({
      locale,
      query,
      location,
      emailAddress,
      jobMailType: mapPositionToJobMailType({ position, pageNumber })
    });
  };

  renderForm = () => {
    const { status, errorMessage, changeHandler, position } = this.props;

    const { emailAddressFocused } = this.state;
    const isSaving = status === SAVING;
    const isError = status === ERROR;

    const inputProps = {
      required: true,
      readOnly: isSaving,
      placeholder: 'Enter your email',
      title: 'Please enter your email',
      'data-hj-masked': true,
      'data-automation': dataAutomationPrefix
    };

    return (
      <form
        data-automation={`${dataAutomationPrefix}-create-${position}`}
        className={styles.form}
        onSubmit={this.jobMailClick}
      >
        <div className={styles.inputField}>
          <EmailField
            className={classnames(styles.email, styles.email_searchResults)}
            inputProps={inputProps}
            onChange={
              isError ? changeHandler : noop
            } /* Add onChange handler only when there has been an error */
            onFocus={this.emailFieldFocusHandler}
            valid={!isError}
            message={errorMessage || false}
            messageProps={{
              'data-automation': `${dataAutomationPrefix}-invalid-message`
            }}
            ref={this.storeEmailFieldReference}
            id="jobMailLiteEmail"
          />
          <Button
            color="blue"
            loading={isSaving}
            type="submit"
            className={styles.button}
            data-automation={`${dataAutomationPrefix}-submit`}
          >
            {isSaving ? 'Saving' : 'Create alert'}
          </Button>
        </div>
        {emailAddressFocused && (
          <Text small role="region" aria-live="assertive">
            You can cancel emails at any time. By clicking "Create alert" you
            agree to&nbsp;
            <NavLink
              component={TextLink}
              analytics="jobmail:privacy"
              location="/privacy"
              children="SEEK’s Privacy ​Statement"
            />
          </Text>
        )}
      </form>
    );
  };

  renderSuccess = () => {
    const { emailAddress } = this.props;

    return (
      <div
        className={styles.message}
        data-automation={`${dataAutomationPrefix}-success-message`}
      >
        <Text raw subheading regular className={styles.thankYou}>
          Alert created
        </Text>
        <Text raw>We’ll send new jobs to:</Text>
        <Text raw>
          <span data-hj-masked={true} className={styles.thankYouEmail}>
            {emailAddress}
          </span>
        </Text>
      </div>
    );
  };

  render() {
    const { status } = this.props;

    return status === SAVED ? this.renderSuccess() : this.renderForm();
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(JobMail);
